import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ErrorBoundary } from "@sentry/react";
import React, { useMemo } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Tooltip } from "react-tippy";
import { useQuery } from "@tanstack/react-query";
import { ErrorMsg } from "../../../../components/AppMessages";
import Collapsible from "../../../../components/Collapsible/Collapsible";
import LoadingEllipsis from "../../../../components/Loading/LoadingEllipsis";
import { DefaultTable } from "../../../../components/Tables/subs";
import { getRatingPerTouchpointCarConfigurator } from "../../../../api/vocApi";

const CarConfiguratorTable = withRouter(({ history }: RouteComponentProps) => {
    const { data, isFetching, refetch } = useQuery({
        queryKey: ["ratingPerTouchpointCarConfigurator", history.location.search],
        queryFn: () => getRatingPerTouchpointCarConfigurator(),
        enabled: false,
    });

    React.useEffect(() => {
        refetch();
    }, [history.location.search]);

    const currentData = data?.Current ?? [];
    const yoyData = data?.YoY ?? [];

    const columns = useMemo(
        () => [
            {
                id: "touchpoints",
                Header: "Touchpoints",
                accessor: "touchpoint",
                enableRowSpan: true,
                sort: false,
                disableSortBy: true,
            },
            {
                id: "metric",
                Header: "Metric",
                accessor: "metric",
                sort: false,
                disableSortBy: true,
            },
            {
                id: "value",
                Header: "Selected period",
                accessor: "value",
                sort: false,
                disableSortBy: true,
                Cell: (cell: { value: number; row: any }) => {
                    const { value } = cell;
                    const metric = cell.row.original.metric;
                    return (
                        <div className={metric === "Rate" ? `${value >= 4 ? "textGreen" : value >= 3.9 ? "textAmber" : "textRed"}` : ""}>
                            {value ? value.toLocaleString() : value != 0 ? value : ""}
                        </div>
                    );
                },
            },
            {
                id: "significant",
                Header: () => (
                    <div style={{ display: "flex" }}>
                        <ArrowDownwardIcon htmlColor="#A9A9A9" className="rtp-significance-arrow" />
                        <Tooltip
                            position="right"
                            className={"normal_tippy"}
                            trigger="mouseenter"
                            animation="none"
                            interactive={true}
                            delay={1}
                            hideDelay={1}
                            duration={1}
                            title="Watch out for low statistical significance. A 'down arrow' signifies a low volume of respondents (lower than 16 per month)"
                            size={"small"}
                        >
                            <span className="info_tooltip" />
                        </Tooltip>
                    </div>
                ),
                accessor: "significant",
                sort: false,
                disableSortBy: true,
                Cell: (cell: any) => {
                    const showIcon = cell.row.values.significant;
                    return !showIcon ? <ArrowDownwardIcon htmlColor="#A9A9A9" className="rtp-significance-arrow" /> : null;
                },
            },
            {
                id: "ytd",
                Header: "Previous period",
                accessor: "yoy_value",
                sort: false,
                disableSortBy: true,
                Cell: (cell: { value: number }) => {
                    const { value } = cell;
                    return <div>{value ? value.toLocaleString() : value != 0 ? value : ""}</div>;
                },
            },
            {
                id: "yoy_significant",
                Header: () => (
                    <div style={{ display: "flex" }}>
                        <ArrowDownwardIcon htmlColor="#A9A9A9" className="rtp-significance-arrow" />
                        <Tooltip
                            position="right"
                            className={"normal_tippy"}
                            trigger="mouseenter"
                            animation="none"
                            interactive={true}
                            delay={1}
                            hideDelay={1}
                            duration={1}
                            title="Watch out for low statistical significance. A 'down arrow' signifies a low volume of respondents (lower than 16 per month)"
                            size={"small"}
                        >
                            <span className="info_tooltip" />
                        </Tooltip>
                    </div>
                ),
                accessor: "yoy_significant",
                sort: false,
                disableSortBy: true,
                Cell: (cell: any) => {
                    const showIcon = cell.row.values.yoy_significant;
                    return !showIcon ? <ArrowDownwardIcon htmlColor="#A9A9A9" className="rtp-significance-arrow" /> : null;
                },
            },
        ],
        [currentData]
    );

    const tableData = useMemo(() => {
        const rows: any[] = [];
        currentData.forEach((item: any) => {
            const yoyEquivalent = yoyData.find((yoyItem: any) => yoyItem.touchpoint === item.touchpoint);
            rows.push(
                {
                    touchpoint: item.touchpoint,
                    metric: "Rate",
                    value: item.rate,
                    significant: true,
                    yoy_value: yoyEquivalent?.pts_rate,
                    yoy_significant: true,
                },
                {
                    touchpoint: item.touchpoint,
                    metric: "# answers",
                    value: item.answers,
                    significant: item.significant,
                    yoy_value: yoyEquivalent?.pts_answers,
                    yoy_significant: yoyEquivalent?.significant,
                }
            );
        });
        return rows;
    }, [currentData, yoyData]);

    return (
        <ErrorBoundary fallback={<ErrorMsg />}>
            {tableData.length > 0 && (
                <div className="grid-col-span-6">
                    <Collapsible title="Car Configurator focus" show={false}>
                        <div className="tile">
                            {isFetching ? (
                                <LoadingEllipsis isLoading={isFetching} />
                            ) : (
                                <DefaultTable
                                    columns={columns}
                                    data={tableData}
                                    id={"carConfiguratorTable"}
                                    isChildComponent={false}
                                    initialSortColId={{ id: "touchpoints", desc: false }}
                                    hiddenCols={[]}
                                    manualSortBy={true}
                                />
                            )}
                        </div>
                    </Collapsible>
                </div>
            )}
        </ErrorBoundary>
    );
});

export default CarConfiguratorTable;